import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import {
  ATLEAST_ONE_CAPITAL_REGEX,
  ATLEAST_ONE_NUMBER_REGEX,
  ATLEAST_ONE_SPECIAL_CHARACTER_REGEX,
  PASSWORD_MIN_LENGTH,
  PASSWORD_MAX_LENGTH,
  ATLEAST_ONE_SMALL_REGEX,
  PHONE_NO_LENGTH,
  EMAIL_REGEX,
  TITLE_MAX_LIMIT,
} from "../../../utils/Constants"
import { PHONE_VALIDATION } from "../../../utils/commonValidations";
import moment from "moment";

export const useValidationSchemas = () => {
  const { t } = useTranslation();

  const loginValidationSchema = Yup.object().shape({
    email: Yup.string()
      .trim()
      .email(t("center.login.label_email_invalid_format_error"))
      .test('no-uppercase', t("center.login.label_email_uppercase_error"), function (value) {
        if (value) {
          return !/[A-Z]/.test(value);
        }
        return true;
      })
      .required(t("center.login.label_email_required_error")),
    password: Yup.string().required(t("center.login.validation_password_required"))
  })

  const forgetPasswordValidationSchema = Yup.object().shape({
    email: Yup.string()
      .trim()
      .email("center.forget_password.label_email_invalid_format_error")
      .test('no-uppercase', "center.forget_password.label_email_uppercase_error", function (value) {
        if (value) {
          return !/[A-Z]/.test(value);
        }
        return true;
      })
      .required("center.forget_password.label_email_required_error"),
  })


  const otpValidationSchema = Yup.object().shape({
    otp: Yup.string()
      .required(t("center.otp_verification.validation_OTP_is_required"))
      .length(4, t("center.otp_verification.otp_error_msg"))
      .matches(/^\d{4}$/, t("center.otp_verification.invalid_otp")),
  });

  const resetPswValidationSchema = Yup.object().shape({
    newPassword: Yup.string()
      .required(t("center.reset_password.label_new_password_error"))
      .matches(
        ATLEAST_ONE_SMALL_REGEX,
        t("center.reset_password.password_validation_lowercase")
      )
      .matches(
        ATLEAST_ONE_CAPITAL_REGEX,
        t("center.reset_password.Password_Validation_uppercase")
      )
      .matches(
        ATLEAST_ONE_NUMBER_REGEX,
        t("center.reset_password.Password_Validation_number")
      )
      .matches(
        ATLEAST_ONE_SPECIAL_CHARACTER_REGEX,
        t("center.reset_password.Password_Validation_special_char")
      )
      .min(PASSWORD_MIN_LENGTH, t("center.reset_password.Password_Validation_minimum_length"))
      .max(
        PASSWORD_MAX_LENGTH,
        t("center.reset_password.assword_Validation_maximum_length")
      ),
    confirmPassword: Yup.string()
      .required(t("center.reset_password.label_confirm_password_error"))
      .oneOf([Yup.ref("newPassword"), null], t("center.reset_password.password_validation_does_not_match")),
  });

  const restaurantProfileValidationSchema = Yup.object({
    centerName: Yup.string().required('Center name is required'),
    centerAddress: Yup.string().required('Location is required'),
    // phone_number: Yup.string()
    //   .test('phone_required', 'center.profile.label_phone_number_required_error', (value) => {
    //     const phoneNumber = (value.split(" "))[1];
    //     return phoneNumber && phoneNumber.length;
    //   }
    //   )
    //   .test('phone_digit_error', 'center.profile.label_phone_number_digits_error', (value) => {
    //     const phoneNumber = (value.split(" "))[1];
    //     return phoneNumber && /^\d+$/.test(phoneNumber);
    //   }
    //   )
    //   .test('phone_min_length', 'center.profile.label_phone_number_min_length_error', (value) => {
    //     const phoneNumber = (value.split(" "))[1];
    //     return phoneNumber && phoneNumber.length >= PHONE_NO_LENGTH.min;
    //   }
    //   )
    //   .test('phone_max_length', 'center.profile.label_phone_number_max_length_error', (value) => {
    //     const phoneNumber = (value.split(" "))[1];
    //     return phoneNumber && phoneNumber.length <= PHONE_NO_LENGTH.max;
    //   }
    //   )
    // ,
    center_logo: Yup.mixed()
      .nullable()
      .test(
        'fileOrUrl',
        'admin.cafes.supported_file_error',
        value => {
          if (!value) return true;
          if (typeof value === 'string') return /^(http|https):\/\/[^\s$.?#].[^\s]*$/gm.test(value);
          return ['image/jpg', 'image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(value.type);
        }
      )
      .test(
        'fileSize',
        'admin.cafes.image_max_size_error',
        value => {
          if (!value || typeof value === 'string') return true;
          return value.size <= 1.5 * 1024 * 1024;
        }
      ),
  });

  const profileValidationSchema = Yup.object({
    email: Yup.string().matches(EMAIL_REGEX, 'restaurant.mail_notification_setting.invalid_email').required('restaurant.mail_notification_setting.label_email_error'),
    opening: Yup.number().oneOf([0, 1], 'Value must be 0 or 1').required('This field is required'),
    settlement: Yup.number().oneOf([0, 1], 'Value must be 0 or 1').required('This field is required'),
    cancel_sales: Yup.number().oneOf([0, 1], 'Value must be 0 or 1').required('This field is required'),
    edit_sales: Yup.number().oneOf([0, 1], 'Value must be 0 or 1').required('This field is required'),
  });


  const CashierPasswordValidationSchema = Yup.object({
    cashier_password: Yup.string()
      .required("restaurant.cafe_profile.validation_password_required")
      .matches(
        ATLEAST_ONE_SMALL_REGEX,
        "restaurant.cafe_profile.password_validation_lowercase"
      )
      .matches(
        ATLEAST_ONE_CAPITAL_REGEX,
        "restaurant.cafe_profile.Password_Validation_uppercase"
      )
      .matches(
        ATLEAST_ONE_NUMBER_REGEX,
        "restaurant.cafe_profile.Password_Validation_number"
      )
      .matches(
        ATLEAST_ONE_SPECIAL_CHARACTER_REGEX,
        "restaurant.cafe_profile.Password_Validation_special_char"
      )
      .min(PASSWORD_MIN_LENGTH, "restaurant.cafe_profile.Password_Validation_minimum_length")
      .max(
        PASSWORD_MAX_LENGTH,
        "restaurant.cafe_profile.Password_Validation_maximum_length"
      )
  });


  const categoryValidationSchema = Yup.object({
    name: Yup.string()
      .trim()
      .required("restaurant.menu.category_name_error"),
    tax_type: Yup.string().required("restaurant.menu.tax_type_error"),
    short_description: Yup.string().trim().required("restaurant.menu.label_short_description_error").max(150, "restaurant.menu.label_short_description_max_length_error"),
  });


  const menuItemsValidationSchema = Yup.object().shape({
    item_name: Yup.string()
      .required('Item name is required').min(3, 'Item name must be 3 characters').max(15, 'Item name must be at most 15 characters'),
    category_id: Yup.string()
      .required('Category ID is required'),
    item_code: Yup.string()
      .required('item code required'),
    short_description: Yup.string()
      .required('Short description is required'),
    price: Yup.number()
      .required('Price is required')
      .moreThan(0, 'Price must be greater than 0'),
    cost_price: Yup.number()
      .required('Cost price is required')
      .moreThan(0, 'Cost price must be greater than 0')
      .test('maxDigits', 'Cost price cannot exceed 10 digits', value => {
        if (!value) return false;
        return value.toString().length <= 10;
      }),
    // consumption_tax_rate: Yup.number()
    //   .required('Consumption tax rate is required')
    //   .moreThan(0, 'consumption tax rate must be greater than 0'),
    // spice_level_needed: Yup.boolean()
    //   .required('Spice level is required'),
    taxable_price: Yup.number()
      .required('Taxable price is required')
      .moreThan(0, 'Taxable price must be greater than 0')
      .test('maxDigits', 'Taxable price cannot exceed 10 digits', value => {
        if (!value) return false;
        return value.toString().length <= 10;
      }),
    non_taxable_price: Yup.number()
      .required('Non taxable price is required')
      .moreThan(0, 'Non taxable price must be greater than 0')
      .test('maxDigits', 'Non taxable price cannot exceed 10 digits', value => {
        if (!value) return false;
        return value.toString().length <= 10;
      }),
    image: Yup.mixed()
      .required('Image is required')
      .test(
        'fileType',
        'Supported formats: JPG, JPEG, PNG, WEBP, GIF',
        value =>
          !value ||
          typeof value === 'string' ||
          ['image/jpg', 'image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(value.type)
      )
      .test(
        'fileSize',
        'File size should be less than 1.5 MB',
        value =>
          !value ||
          typeof value === 'string' ||
          value.size <= 1.5 * 1024 * 1024
      )
      .test(
        'isValidUrlOrFile',
        'Invalid URL format',
        value => !value || typeof value !== 'string' || /^(ftp|http|https):\/\/[^ "]+$/.test(value)
      ),
    // type: Yup.string()
    //   .required('Type is required'),
    // addons: Yup.array().when('type', ([type], schema) => {
    //   return type === "set_of_items" ? Yup.array().min(1, 'addons_minimum_one_required').of(
    //     Yup.object().shape({
    //       option_id: Yup.string().required('addon_required_error'),
    //       item_id: Yup.string().required('addon_item_required_error'),
    //     })
    //   ) : schema;
    // }),
    // vat: Yup.number()
    // .required('VAT is required')
    // .test('exact-length', 'VAT must be exactly 15 digits', value => 
    //     value && value.toString().length === 15
    // ),
    // type: Yup.string().required('Type is required'),
  });


  const staffValidationSchema = Yup.object({
    first_name: Yup.string().trim().required("center.staff.label_first_name_error").max(30, "First name cannot be more than 30 characters"),
    last_name: Yup.string().trim().required("center.staff.label_last_name_error").max(30, "Last name cannot be more than 30 characters"),
    dob: Yup.string().trim().required("DOB is required"),
    email: Yup.string().trim().required('center.staff.label_email_error').matches(EMAIL_REGEX, 'center.staff.invalid_email'),
    phone_number: Yup.string()
      .test('phone_required', 'center.staff.label_phone_number_required_error', (value) => {
        console.log("value", value)
        const phoneNumber = value ? (value.split(" "))[1] : null;
        return phoneNumber && phoneNumber.length;
      }
      )
      .test('phone_digit_error', 'center.staff.label_phone_number_digits_error', (value) => {
        const phoneNumber = value ? (value.split(" "))[1] : null;
        return phoneNumber && /^\d+$/.test(phoneNumber);
      }
      )
      .test('phone_min_length', 'center.staff.label_phone_number_min_length_error', (value) => {
        const phoneNumber = value ? (value.split(" "))[1] : null;
        return phoneNumber && phoneNumber.length >= PHONE_NO_LENGTH.min;
      }
      )
      .test('phone_max_length', 'center.staff.label_phone_number_max_length_error', (value) => {
        const phoneNumber = value ? (value.split(" "))[1] : null;
        return phoneNumber && phoneNumber.length <= PHONE_NO_LENGTH.max;
      }
      ),
    password: Yup.string()
      .required(t("restaurant.staff.validation_password_required"))
      .matches(
        ATLEAST_ONE_SMALL_REGEX,
        t("restaurant.staff.password_validation_lowercase")
      )
      .matches(
        ATLEAST_ONE_CAPITAL_REGEX,
        t("restaurant.staff.Password_Validation_uppercase")
      )
      .matches(
        ATLEAST_ONE_NUMBER_REGEX,
        t("restaurant.staff.Password_Validation_number")
      )
      .matches(
        ATLEAST_ONE_SPECIAL_CHARACTER_REGEX,
        t("restaurant.staff.Password_Validation_special_char")
      )
      .min(PASSWORD_MIN_LENGTH, t("restaurant.staff.Password_Validation_minimum_length"))
      .max(
        PASSWORD_MAX_LENGTH,
        t("restaurant.staff.Password_Validation_maximum_length")
      ),
    // profile_image: Yup.mixed()
    // .nullable()
    // .test(
    //   'fileOrUrl',
    //   'admin.cafes.supported_file_error',
    //   value => {
    //     if (!value) return true;
    //     if (typeof value === 'string') return /^(http|https):\/\/[^\s$.?#].[^\s]*$/gm.test(value);
    //     return ['image/jpg', 'image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(value.type);
    //   }
    // )
    // .test(
    //   'fileSize',
    //   'admin.cafes.image_max_size_error',
    //   value => {
    //     if (!value || typeof value === 'string') return true;
    //     return value.size <= 1.5 * 1024 * 1024;
    //   }
    // ),
    profile_image: Yup.mixed()
      .nullable()
      .test(
        'fileOrUrl',
        'admin.cafes.supported_file_error',
        value => {
          if (!value) return true;
          if (typeof value === 'string') return /^(http|https):\/\/[^\s$.?#].[^\s]*$/gm.test(value);
          return ['image/jpg', 'image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(value.type);
        }
      )
      .test(
        'fileSize',
        'admin.cafes.image_max_size_error',
        value => {
          if (!value || typeof value === 'string') return true;
          return value.size <= 1.5 * 1024 * 1024;
        }
      )
      .test(
        'validImage',
        'Upload a valid image',
        value =>
          new Promise(resolve => {
            if (!value || typeof value === 'string') return resolve(true);

            const reader = new FileReader();
            reader.onload = () => {
              const img = new Image();
              img.onload = () => resolve(true);
              img.onerror = () => resolve(false);
              img.src = reader.result;
            };
            reader.onerror = () => resolve(false);
            reader.readAsDataURL(value);
          })
      ),
  });


  
  const editStaffValidationSchema = Yup.object({
    employee_id: Yup.string().trim().required("restaurant.staff.employee_id_error"),
    first_name: Yup.string().trim().required("restaurant.staff.label_first_name_error").max(30, "First name cannot be more than 30 characters"),
    last_name: Yup.string().trim().required("restaurant.staff.label_last_name_error").max(30, "Last name cannot be more than 30 characters"),
    // staff_joining_date: Yup.string().trim().required("restaurant.staff.label_joining_date_error"),
    // staff_state: Yup.string(),
    // staff_zip_code: Yup.string(),
    // staff_city: Yup.string(),
    // address: Yup.string().trim(),
    email: Yup.string().trim().required('restaurant.staff.label_email_error').matches(EMAIL_REGEX, 'restaurant.staff.invalid_email'),
    phone_number: Yup.string()
      .test('phone_required', 'restaurant.staff.label_phone_number_required_error', (value) => {
        console.log("value", value)
        const phoneNumber = value ? (value.split(" "))[1] : null;
        return phoneNumber && phoneNumber.length;
      }
      )
      .test('phone_digit_error', 'restaurant.staff.label_phone_number_digits_error', (value) => {
        const phoneNumber = value ? (value.split(" "))[1] : null;
        return phoneNumber && /^\d+$/.test(phoneNumber);
      }
      )
      .test('phone_min_length', 'restaurant.staff.label_phone_number_min_length_error', (value) => {
        const phoneNumber = value ? (value.split(" "))[1] : null;
        return phoneNumber && phoneNumber.length >= PHONE_NO_LENGTH.min;
      }
      )
      .test('phone_max_length', 'restaurant.staff.label_phone_number_max_length_error', (value) => {
        const phoneNumber = value ? (value.split(" "))[1] : null;
        return phoneNumber && phoneNumber.length <= PHONE_NO_LENGTH.max;
      }
      ),
    password: Yup.string()
      .matches(
        ATLEAST_ONE_SMALL_REGEX,
        t("restaurant.staff.password_validation_lowercase")
      )
      .matches(
        ATLEAST_ONE_CAPITAL_REGEX,
        t("restaurant.staff.Password_Validation_uppercase")
      )
      .matches(
        ATLEAST_ONE_NUMBER_REGEX,
        t("restaurant.staff.Password_Validation_number")
      )
      .matches(
        ATLEAST_ONE_SPECIAL_CHARACTER_REGEX,
        t("restaurant.staff.Password_Validation_special_char")
      )
      .min(PASSWORD_MIN_LENGTH, t("restaurant.staff.Password_Validation_minimum_length"))
      .max(
        PASSWORD_MAX_LENGTH,
        t("restaurant.staff.Password_Validation_maximum_length")
      ),
    profile_image: Yup.mixed()
      .nullable()
      .test(
        'fileOrUrl',
        'admin.cafes.supported_file_error',
        value => {
          if (!value) return true;
          if (typeof value === 'string') return /^(http|https):\/\/[^\s$.?#].[^\s]*$/gm.test(value);
          return ['image/jpg', 'image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(value.type);
        }
      )
      .test(
        'fileSize',
        'admin.cafes.image_max_size_error',
        value => {
          if (!value || typeof value === 'string') return true;
          return value.size <= 1.5 * 1024 * 1024;
        }
      )
      .test(
        'validImage',
        'Upload a valid image',
        value =>
          new Promise(resolve => {
            if (!value || typeof value === 'string') return resolve(true);

            const reader = new FileReader();
            reader.onload = () => {
              const img = new Image();
              img.onload = () => resolve(true);
              img.onerror = () => resolve(false);
              img.src = reader.result;
            };
            reader.onerror = () => resolve(false);
            reader.readAsDataURL(value);
          })
      ),
  });


  const tableValidationSchema = Yup.object({
    table_number: Yup.string()
      .required("Table number is required")
      .max(20, "Table number cannot be more than 20 characters"),
    capacity: Yup.number().required("restaurant.table_management.table_capacity_error")
      .min(1, "restaurant.table_management.label_table_capacity_error")
      .max(9999999999, "Table capacity can not more than 10 digit")
      .integer("restaurant.table_management.label_capacity_digits_error"),
    // capacity: Yup.number()
    //   .required("restaurant.table_management.table_capacity_error")
    //   .test('capacity_digit_error', 'restaurant.table_management.number_err_msg', (value) => {
    //     const capacity = value ? parseFloat(value) : null;
    //     return capacity !== null && capacity >= 0; // Check if the value is non-negative
    //   })
    //   .test('capacity_digit_error', 'restaurant.table_management.label_capacity_digits_error', (value) => {
    //     const capacity = value ? parseFloat(value) : null;
    //     return capacity !== null && Number.isInteger(capacity); // Check if the value is an integer
    //   }),

    shape: Yup.string().trim().required('restaurant.table_management.table_shape_error')
  });

  const serviceChargeValidationSchema = Yup.object().shape({
    service_charge: Yup.string()
      .trim()
      .required("restaurant.service_charges.service_charge_required")  // Required field
      .test('is-non-negative', 'restaurant.service_charges.service_charge_negative_error', (value) => {
        const serviceCharge = value ? parseFloat(value) : null;
        return serviceCharge !== null && serviceCharge >= 0 && Number.isInteger(serviceCharge);  // Check non-negative and integer
      })
      .test('is-within-range', 'restaurant.service_charges.service_charge_limit_error', (value) => {
        const serviceCharge = value ? parseInt(value) : null;
        return serviceCharge !== null && serviceCharge >= 0 && serviceCharge <= 100;  // Check within range 0 to 100
      })
  });



  const addResevationValidationSchema = Yup.object().shape({
    table_id: Yup.string().trim().required("restaurant.table_management.table_error"),
    table_number: Yup.string().trim(),
    number_of_guest: Yup.string().trim()
      .required("restaurant.table_management.table_capacity_error")
      .test('capacity_digit_error', 'restaurant.table_management.number_err_msg', (value) => {
        const capacity = value ? parseFloat(value) : null;
        return capacity !== null && capacity >= 0; // Check if the value is non-negative
      })
      .test('capacity_digit_error', 'restaurant.table_management.label_capacity_digits_error_guests', (value) => {
        const capacity = value ? parseFloat(value) : null;
        return capacity !== null && Number.isInteger(capacity); // Check if the value is an integer
      }),
    customer_name: Yup.string().trim().required("restaurant.table_management.label_customer_name_error"),
    customer_email: Yup.string().trim().required('restaurant.table_management.label_email_error').matches(EMAIL_REGEX, 'restaurant.table_management.invalid_email'),
    customer_phone_number: Yup.string()
      .test('phone_required', 'restaurant.table_management.label_phone_number_required_error', (value) => {
        const phoneNumber = value ? (value.split(" "))[1] : null;
        return phoneNumber && phoneNumber.length;
      }
      )
      .test('phone_digit_error', 'restaurant.table_management.label_phone_number_digits_error', (value) => {
        const phoneNumber = value ? (value.split(" "))[1] : null;
        return phoneNumber && /^\d+$/.test(phoneNumber);
      }
      )
      .test('phone_min_length', 'restaurant.table_management.label_phone_number_min_length_error', (value) => {
        const phoneNumber = value ? (value.split(" "))[1] : null;
        return phoneNumber && phoneNumber.length >= PHONE_NO_LENGTH.min;
      }
      )
      .test('phone_max_length', 'restaurant.table_management.label_phone_number_max_length_error', (value) => {
        const phoneNumber = value ? (value.split(" "))[1] : null;
        return phoneNumber && phoneNumber.length <= PHONE_NO_LENGTH.max;
      }
      ),

    reservation_date: Yup.string().trim().required("restaurant.table_management.label_date_error"),
    start_time: Yup.string().trim().required("restaurant.table_management.label_start_time_error"),
    end_time: Yup.string()
      .required('restaurant.table_management.label_end_time_error')
      .test('greater_time', 'restaurant.table_management.rest_validation_end_time_should_be_greater_than_start_time', function (value) {
        if (this.parent?.start_time && value) {
          try {
            const openDate = new Date(this.parent?.start_time);
            const closeDate = new Date(value);
            openDate.setSeconds(0);
            openDate.setMilliseconds(0);
            closeDate.setSeconds(0);
            closeDate.setMilliseconds(0);
            return closeDate.getTime() > openDate.getTime();
          } catch (err) {
            console.log("err", err);
          }
        }
        return true;
      }),
  });

  const addMedicalHistoryValidationSchema = Yup.object({
    medical_history_document: Yup.array()
    .min(1, 'At least one document is required')
    .of(Yup.mixed().required('File is required'))
    .required('Document is required'),  
    medical_history_note: Yup.string().trim().required('History note is required'),
  });





  // number_err_msg label_capacity_digits_error
  return {
    loginValidationSchema,
    forgetPasswordValidationSchema,
    resetPswValidationSchema,
    otpValidationSchema,
    restaurantProfileValidationSchema,
    CashierPasswordValidationSchema,
    profileValidationSchema,
    categoryValidationSchema,
    menuItemsValidationSchema,
    staffValidationSchema,
    editStaffValidationSchema,
    tableValidationSchema,
    serviceChargeValidationSchema,
    addResevationValidationSchema,
    addMedicalHistoryValidationSchema
  };
};


